import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import { ProviderToName } from 'components/ArticlePostPreview/PostPreviewBody/IntegrationsDrawer/constants'
import { ChannelItemState } from './ChannelListItem'
import { hasContentWarning } from './channelWarnings'
import { isStoryChannel } from '../utils'

const STORY_LIFETIME_MS = 24 * 60 * 60 * 1000

const isExpiredStory = (channel: DesignChannel | ChannelType | undefined, postedAt: string | undefined) => {
  if (!isStoryChannel(channel)) return false
  if (!postedAt) return false
  const postedMs = Date.parse(postedAt)
  if (Number.isNaN(postedMs)) return false
  return Date.now() - postedMs >= STORY_LIFETIME_MS
}

const expiredStoryTooltip = (channel: DesignChannel | ChannelType | undefined) => {
  const network = channel === DesignChannel.FacebookStory ? 'Facebook' : 'Instagram'
  return `Since 24 hours have passed, your Story is no longer live. You can still find it in your ${network} archive.`
}

const deriveState = (
  isConnected: boolean,
  target: ScheduledPostTarget | undefined,
  isPosted: boolean,
  channel: DesignChannel | ChannelType | undefined,
): ChannelItemState => {
  if (isPosted && target?.status === 'published') {
    return isExpiredStory(channel, target.published_at) ? 'posted-expired' : 'posted'
  }
  if (target?.status === 'failed') return 'failed'
  if (target?.status === 'queued' || target?.status === 'processing' || target?.status === 'retrying') {
    return 'processing'
  }
  if (!isConnected) return 'unconnected'
  if (isPosted && target) return 'post-now'
  if (target && hasContentWarning(target)) return 'warning'
  return target ? 'connected' : 'connected-disabled'
}

export interface ChannelRowData {
  provider: ExternalIntegrationEnum
  integrationId?: number
  accountName?: string
  accountAvatarUrl?: string
  state: ChannelItemState
  postUrl?: string
  expiredTooltip?: string
}

interface BuildChannelRowsArgs {
  crossPostGroup: readonly ExternalIntegrationEnum[]
  integrations: ExternalIntegration[]
  targets: ScheduledPostTarget[]
  accountIdsNeedReauth: number[]
  isPosted: boolean
  channel: DesignChannel | ChannelType | undefined
}

export const buildChannelRows = ({
  crossPostGroup,
  integrations,
  targets,
  accountIdsNeedReauth,
  isPosted,
  channel,
}: BuildChannelRowsArgs): ChannelRowData[] => {
  const rows: ChannelRowData[] = []

  for (const provider of crossPostGroup) {
    const providerIntegrations = integrations.filter((i) => i.provider === provider)

    if (providerIntegrations.length >= 1) {
      for (const integration of providerIntegrations) {
        const target = targets.find((t) => t.provider === provider && t.external_integration_id === integration.id)
        const needsReauth = accountIdsNeedReauth.includes(integration.id)
        const { name, avatar } = extractProfileInfo(integration)
        const state = deriveState(!needsReauth, target, isPosted, channel)
        rows.push({
          provider,
          integrationId: integration.id,
          accountName: name || integration.name || ProviderToName[provider],
          accountAvatarUrl: avatar || integration.picture || undefined,
          state,
          postUrl: target?.post_url,
          expiredTooltip: state === 'posted-expired' ? expiredStoryTooltip(channel) : undefined,
        })
      }
      continue
    }

    const state = deriveState(false, undefined, isPosted, channel)
    rows.push({
      provider,
      integrationId: undefined,
      accountName: undefined,
      accountAvatarUrl: undefined,
      state,
      postUrl: undefined,
      expiredTooltip: undefined,
    })
  }

  return rows
}
